import { fetchSubscriptionStatusViaBff } from '~/utils/billing'
import type { KeycloakTokenParsed } from 'keycloak-js'
import type { RouteLocationNormalized } from 'vue-router'
import { resolveStripeEnabled } from '~/components/Utilities/billingFeatureFlag'
import { hasBillingAccess } from '~~/shared/billing/subscription-status'
import {
  BILLING_ROUTE_PREFIX,
  BILLING_SUCCESS_ROUTE,
  HOME_ROUTE,
} from '~~/shared/routes'

export async function enforceSubscriptionAccess(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  token?: string,
  tokenParsed?: KeycloakTokenParsed
) {
  const runtimeConfig = useRuntimeConfig()
  // TODO: Remove this once the Stripe flow is enabled
  if (!resolveStripeEnabled(runtimeConfig.public)) {
    return
  }

  if (!token) {
    return
  }

  // The success page manages its own status polling and handles navigation to
  // /home once the subscription is confirmed. Always allow it through so it
  // is never redirected back to /billing while the pipeline is still settling.
  if (to.path === BILLING_SUCCESS_ROUTE) {
    return
  }

  const roles = tokenParsed?.realm_access?.roles ?? []
  const isNavigatingFromBilling = from.path.startsWith(BILLING_ROUTE_PREFIX)
  const response = await fetchSubscriptionStatusViaBff(token, {
    forceRefresh: isNavigatingFromBilling,
  })
  const hasAccess = hasBillingAccess(response.status, roles)

  if (to.path.startsWith(BILLING_ROUTE_PREFIX)) {
    if (hasAccess) {
      return navigateTo(HOME_ROUTE, { replace: true })
    }
    return
  }

  if (hasAccess) {
    return
  }

  // Cached status may be stale (60s BFF cache) — verify before blocking
  if (!isNavigatingFromBilling) {
    const freshResponse = await fetchSubscriptionStatusViaBff(token, {
      forceRefresh: true,
    })
    if (hasBillingAccess(freshResponse.status, roles)) {
      return
    }
  }

  return navigateTo(BILLING_ROUTE_PREFIX, { replace: true })
}
